<template>
  <div>
    <Panel module="Quadro" layout="large" :showFilter="false" :showSearch="false" :showButtons="false">
      <div slot="content-main">
        <br>
        <div class="planner-main-toolbar">
          <Button
            _key="btnFunnelCreate"
            title="Novo Quadro"
            classIcon="fa-solid fa-plus"
            type="primary"
            size="small"
            :clicked="openCreateModal"
          />
        </div>

        <Loading v-if="loading" type="line" :center="false" class="planner-main-loading" />

        <div v-if="!loading && funnels.length === 0" class="planner-main-empty">
          <i class="fa-solid fa-chart-kanban"></i>
          <span>Nenhum quadro encontrado. Crie um para começar!</span>
        </div>

        <div v-if="funnels.length > 0" class="planner-main-grid">
          <FunnelCard
            v-for="funnel in funnels"
            :key="funnel.id"
            :funnel="funnel"
            @open="navigateToKanban"
            @edit="openEditModal"
            @delete="openDeleteModal"
          />
        </div>
      </div>
    </Panel>

    <Modal title="Quadro" :width="800" :height="750" v-show="showModal('funnelForm')">
      <FunnelForm />
    </Modal>

    <Modal title="Excluir Quadro" :width="500" v-if="showModal('funnelDelete')">
      <div>
        <p class="planner-main-delete-text">Tem certeza que deseja excluir o quadro <b>{{ funnelToDelete.name }}</b>?</p>

        <Alert type="danger">
          Esta ação não pode ser desfeita. Todas as etapas e
          <b>{{ funnelToDelete.totalCards || 0 }}</b> cards deste quadro serão excluídos, junto com
          comentários, checklists e compromissos vinculados.
        </Alert>

        <div class="planner-main-delete-confirm">
          <p class="planner-main-delete-instruction">
            Para confirmar, digite <b>remover</b> no campo abaixo:
          </p>
          <InputText
            field="deleteConfirmation"
            placeholder="remover"
            :formName="formName"
            :maxLength="20"
            v-model="deleteConfirmation"
          />
        </div>

        <div class="text-center">
          <Button
            _key="btnConfirmDelete"
            type="danger"
            title="Sim, excluir"
            classIcon="fa-solid fa-trash"
            size="medium"
            :disabled="!isDeleteConfirmed"
            :clicked="confirmDelete"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import Alert from "@/ui/layout/Alert";
import Panel from "@/ui/layout/Panel";

import Button from "@/ui/forms/Button";
import InputText from "@/ui/forms/InputText";
import Modal from "@/ui/forms/Modal";

import Loading from "@/ui/shared/Loading.vue";

import FunnelCard from "../components/funnel/FunnelCard.vue";
import FunnelForm from "../components/funnel/FunnelForm.vue";

import FunnelService from "../services/funnel.service";

import { mapGetters, mapMutations, mapState } from "vuex";

export default {
  name: "PlannerMain",
  components: { Alert, Panel, Loading, Button, InputText, Modal, FunnelCard, FunnelForm },
  data() {
    return {
      formName: "funnelDeleteForm",
      funnels: [],
      funnelToDelete: {},
      deleteConfirmation: "",
      loading: true,
    };
  },
  mounted() {
    this.removeLoading(["panel"]);
    this.loadFunnels();
  },
  computed: {
    ...mapState("user", ["userLogged"]),
    ...mapGetters("generic", ["showModal", "event"]),
    isDeleteConfirmed() {
      return this.deleteConfirmation.trim().toLowerCase() === "remover";
    },
  },
  methods: {
    ...mapMutations("generic", ["addEvent", "openModal", "hideModal", "removeLoading"]),
    async loadFunnels() {
      this.loading = true;
      const response = await FunnelService.getAll(this.userLogged.user.id);
      this.funnels = response.data.content.data;
      this.loading = false;
    },
    openCreateModal() {
      this.openModal("funnelForm");
      this.addEvent({ name: "funnelCreate" });
      this.removeLoading(["btnFunnelCreate"]);
    },
    openEditModal(funnel) {
      this.openModal("funnelForm");
      this.addEvent({ name: "funnelUpdate", data: funnel });
    },
    openDeleteModal(funnel) {
      this.funnelToDelete = funnel;
      this.deleteConfirmation = "";
      this.openModal("funnelDelete");
    },
    async confirmDelete() {
      if (!this.isDeleteConfirmed) return;

      try {
        await FunnelService.remove([this.funnelToDelete.id]);
        this.deleteConfirmation = "";
        this.hideModal();
        await this.loadFunnels();
      } finally {
        this.removeLoading(["btnConfirmDelete"]);
      }
    },
    navigateToKanban(funnel) {
      this.$router.push({
        name: "plannerKanban",
        params: { id: funnel.id, name: funnel.name },
      });
    },
  },
  watch: {
    event: {
      handler(event) {
        if (event.name === "funnelCreated" || event.name === "funnelUpdated") {
          this.loadFunnels();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.planner-main-loading {
  margin-bottom: 20px;
}

.planner-main-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-top: 8px;
}

.planner-main-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
  color: var(--text-muted);
  font-size: 14px;
}

.planner-main-empty i {
  font-size: 40px;
  color: var(--color-brand-light);
}

.planner-main-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.planner-main-delete-text {
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 20px;
  text-align: center;
}

.planner-main-delete-confirm {
  margin-top: 16px;
  margin-bottom: 8px;
}

.planner-main-delete-instruction {
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}
</style>

